import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  useActionItems,
  useCreateActionItem,
  useDeleteActionItem,
  useUpdateActionItem,
  type ActionItem,
  type ActionItemSource,
  type ActionItemStatus,
} from "../api";
import {
  ACTION_SOURCE_LABEL,
  ACTION_STATUS_META,
  ACTION_STATUS_VARIANT,
  VARIANT_BG_CLASS,
} from "../constants";
import { UserPicker } from "./UserPicker";

const NEXT_STATUS: Record<ActionItemStatus, ActionItemStatus> = {
  open: "in_progress",
  in_progress: "done",
  done: "open",
  cancelled: "open",
};

export function ActionItemsPanel({ quarterId }: { quarterId: string }) {
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const items = useActionItems({ quarterId, ownerId: ownerFilter ?? undefined });
  const create = useCreateActionItem();

  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState<string | null>(null);
  const [source, setSource] = useState<ActionItemSource>("exec_meeting");

  function add() {
    const t = title.trim();
    if (!t) return;
    create.mutate(
      { title: t, owner_id: owner, source, quarter_id: quarterId },
      { onSuccess: () => setTitle("") },
    );
  }

  return (
    <aside className="flex w-full flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground shadow-sm lg:w-80">
      <div>
        <h2 className="text-sm font-bold">Action items</h2>
        <p className="text-xs text-muted-foreground">From exec meetings & 1:1s</p>
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-2">
        <Input
          placeholder="New action item…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <div className="flex gap-2">
          <UserPicker value={owner} onChange={setOwner} className="h-8 flex-1 rounded-xl border border-input bg-background px-2 text-xs" />
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as ActionItemSource)}
            className="h-8 rounded-xl border border-input bg-background px-1 text-xs"
          >
            <option value="exec_meeting">Exec</option>
            <option value="one_on_one">1:1</option>
            <option value="manual">Manual</option>
          </select>
          <Button type="button" size="sm" className="h-8" onClick={add} disabled={!title.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Owner</span>
        <UserPicker
          value={ownerFilter}
          onChange={setOwnerFilter}
          className="h-7 flex-1 rounded-xl border border-input bg-background px-2 text-xs"
        />
      </div>

      <div className="flex flex-col gap-2">
        {items.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {items.data?.length === 0 && (
          <p className="text-xs text-muted-foreground">No action items yet.</p>
        )}
        {items.data?.map((item) => (
          <ActionItemRow key={item.id} item={item} />
        ))}
      </div>
    </aside>
  );
}

function ActionItemRow({ item }: { item: ActionItem }) {
  const update = useUpdateActionItem();
  const remove = useDeleteActionItem();
  const label = ACTION_STATUS_META.find((s) => s.key === item.status)?.label ?? item.status;
  const variant = ACTION_STATUS_VARIANT[item.status] ?? "neutral";
  const done = item.status === "done";

  return (
    <div className="flex items-start gap-2 rounded-md border border-border p-2">
      <button
        type="button"
        title="Cycle status"
        onClick={() => update.mutate({ id: item.id, payload: { status: NEXT_STATUS[item.status] } })}
        className={cn("mt-0.5 h-3 w-3 flex-shrink-0 rounded-full", VARIANT_BG_CLASS[variant])}
      />
      <div className="min-w-0 flex-1">
        <div className={cn("text-sm", done && "text-muted-foreground line-through")}>
          {item.title}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>{item.owner_name ?? "Unassigned"}</span>
          <span>·</span>
          <span>{ACTION_SOURCE_LABEL[item.source] ?? item.source}</span>
          <span>·</span>
          <span>{label}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => remove.mutate(item.id)}
        className="text-muted-foreground hover:text-destructive"
        aria-label="Delete action item"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
